import type { CurrencyConfig } from './currency-helpers.type';

/**
 * Unicode HAIR SPACE character (U+200A)
 * A very thin space for improved typographic elegance between currency symbol and value
 */
const HAIR_SPACE = '\u200A';

/**
 * Default currency configuration for USD (US Dollar)
 * Follows Bloomfilter's formatting requirements:
 * - Dollar sign ($) prefix
 * - HAIR SPACE between symbol and value
 * - Comma thousands separators
 * - Always 2 decimal places
 */
const DEFAULT_CURRENCY_CONFIG: CurrencyConfig = {
  symbol: '$',
  placement: 'prefix',
  useHairSpace: true,
  locale: 'en-US',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
};

/**
 * Currency configurations for different currencies and locales
 * Prepared for future multi-currency support
 */
const CURRENCY_CONFIGS: Record<string, CurrencyConfig> = {
  USD: {
    symbol: '$',
    placement: 'prefix',
    useHairSpace: true,
    locale: 'en-US',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  },
  EUR: {
    symbol: '€',
    placement: 'prefix',
    useHairSpace: true,
    locale: 'en-US',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  },
  EUR_EU: {
    symbol: '€',
    placement: 'suffix',
    useHairSpace: true,
    locale: 'de-DE',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  },
  GBP: {
    symbol: '£',
    placement: 'prefix',
    useHairSpace: true,
    locale: 'en-GB',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  },
};

/**
 * Default fallback value for currency formatting errors
 */
const DEFAULT_FALLBACK = '$\u200A0.00';

/**
 * Error messages for currency validation
 */
const ERROR_MESSAGES = {
  INVALID_INPUT: 'Invalid currency input',
  INVALID_NUMBER: 'Input cannot be parsed as a valid number',
  INVALID_CURRENCY_CODE: 'Invalid currency code',
  INVALID_LOCALE: 'Invalid locale',
  PARSING_ERROR: 'Error parsing currency value',
  FORMATTING_ERROR: 'Error formatting currency value',
} as const;

export { CURRENCY_CONFIGS, DEFAULT_CURRENCY_CONFIG, DEFAULT_FALLBACK, ERROR_MESSAGES, HAIR_SPACE };
